/**
 * Premium bundle presentation for Crown01.
 *
 * Auto-activates for any Shopify product whose `tags` include `bundle`
 * (case-insensitive). All copy is derived from the Shopify product —
 * no per-bundle code changes ever needed:
 *
 *   • Bundle name        → product.name
 *   • Hero image         → product.image
 *   • Bundle price       → variant.price (default variant)
 *   • Compare-at price   → variant.compare_at_price  (set in Shopify to show "Save £X")
 *   • Bundle contents    → extracted from product.description_html (any <li>
 *                          elements) OR `- ` / `•` bullet lines in the plain
 *                          description. Pad to the first 5–8 items shown.
 *
 * To create a new bundle: in Shopify Admin → Products → New, set price + compare-at
 * (e.g. 72.20 / 76.00), tag it `bundle`, put the contained items in the description
 * as a bulleted list. Publish — it appears in the storefront's Bundle Deals
 * section within 5 minutes (or instantly via POST /api/cache/clear).
 */
import React from "react";
import { Link } from "react-router-dom";

/** Returns true if a product is tagged as a bundle in Shopify. */
export function isBundleProduct(p) {
  if (!p || !Array.isArray(p.tags)) return false;
  return p.tags.some((t) => String(t).toLowerCase().trim() === "bundle");
}

/** Bundle theme variants. Driven entirely by Shopify tags so new bundles need zero code.
 *
 *  • Default ("trade")  — gold border on dark gradient. Built for B2B/catering buyers.
 *    Triggered by: `bundle` (alone) or `bundle, trade`, `bundle, catering`, `bundle, takeaway`.
 *
 *  • "household"        — red border on warm cream. Soft, friendly look for B2C / home use.
 *    Triggered by: `bundle, household` (or any of: `home`, `discovery`, `trial`).
 */
const HOUSEHOLD_TAGS = new Set(["household", "home", "discovery", "trial"]);
export function getBundleTheme(p) {
  const tags = (p?.tags || []).map((t) => String(t).toLowerCase().trim());
  if (tags.some((t) => HOUSEHOLD_TAGS.has(t))) return "household";
  return "trade";
}

/** Per-bundle savings fallback. Used only when Shopify's variant.compare_at_price
 *  is not set on a bundle product (Shopify Admin → Product → Variant →
 *  Compare-at price). Once compare_at_price is set in Shopify, this fallback
 *  is ignored automatically — Shopify data always wins. Keyed by product slug.
 *  Add a new entry when launching a new bundle, OR set compare_at_price in
 *  Shopify and this whole map becomes redundant.
 */
const BUNDLE_COMPARE_AT_FALLBACK = {
  "crown01-household-essentials-bundle": 28.48,
  "cafe-takeaway-starter-bundle": 76.00,
};

/** Resolve the effective compare-at price for a bundle. Prefers Shopify data;
 *  falls back to BUNDLE_COMPARE_AT_FALLBACK[slug] when Shopify hasn't been
 *  configured. Returns null when no saving data is available. */
export function resolveBundleCompareAt(p, variant) {
  const fromShopify = Number(variant?.compare_at_price || 0);
  const price = Number(variant?.price ?? p?.min_price ?? 0);
  if (fromShopify > price) return fromShopify;
  const fallback = Number(BUNDLE_COMPARE_AT_FALLBACK[p?.slug] || 0);
  if (fallback > price) return fallback;
  return null;
}

/** Pull the bundle contents from the Shopify product description.
 *  Prefers <li> tags in the rich-text HTML; falls back to lines that start
 *  with "- " or "•" in the plain description. Returns an array of strings. */
export function extractBundleContents(p) {
  const html = p?.description_html || "";
  if (html) {
    // Extract every <li>…</li>, strip inner tags, collapse whitespace.
    const matches = Array.from(html.matchAll(/<li[^>]*>([\s\S]*?)<\/li>/gi));
    const items = matches
      .map((m) => m[1].replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim())
      .filter(Boolean);
    if (items.length) return items;
  }
  const text = (p?.description || "").split("\n");
  return text
    .map((line) => line.trim())
    .filter((line) => /^[-•·*]\s+/.test(line))
    .map((line) => line.replace(/^[-•·*]\s+/, "").trim())
    .filter(Boolean);
}

function Money({ value, className = "" }) {
  return <span className={className}>£{Number(value || 0).toFixed(2)}</span>;
}

/** Premium bundle card used inside product grids (Shop page, Bundle Deals strip). */
export function BundleCard({ p, useCart }) {
  const { add } = useCart();
  const variant = p.variants?.find((v) => v.available) || p.variants?.[0];
  const price = variant?.price ?? p.min_price ?? 0;
  const compareAt = resolveBundleCompareAt(p, variant);
  const hasSaving = !!compareAt && compareAt > price;
  const saving = hasSaving ? compareAt - price : 0;
  const savingPct = hasSaving ? Math.round((saving / compareAt) * 100) : 0;
  // Use the labelled-section parser so "What's inside" only ever shows the
  // genuine `Includes:` list (SKUs), never the `Perfect for:` / `Ideal for:`
  // audience list. Returns [] when the Shopify description has no Includes
  // section — in that case the WHAT'S INSIDE block is hidden entirely.
  const items = parseBundleDescription(p?.description_html || "", p?.name).included;
  const theme = getBundleTheme(p);

  // Theme tokens — only colours / labels differ. Layout is identical so both
  // themes behave the same on mobile + grids.
  const T = theme === "household"
    ? {
        wrapStyle: {
          background: "linear-gradient(180deg, #FFFBF3 0%, #F5EAD7 100%)",
          border: "1px solid var(--c-red)",
          boxShadow: "0 16px 40px -18px rgba(220,38,38,0.30), 0 1px 0 rgba(220,38,38,0.10) inset",
          color: "var(--c-black)",
        },
        ribbon: "linear-gradient(90deg, var(--c-red) 0%, #f06363 50%, var(--c-red) 100%)",
        topBadge: {
          label: "Household Favourite",
          icon: "fa-house-heart",
          style: { background: "var(--c-red)", color: "#fff", border: "1px solid var(--c-red)" },
        },
        savingBadge: { background: "var(--c-black)", color: "var(--c-gold)" },
        eyebrow: "Discovery bundle",
        eyebrowColor: "var(--c-red)",
        title: "text-black",
        sub: "text-[color:var(--c-grey-700)]",
        subText: "Try our full range before committing to larger quantities — find the refuse sack that works best for your household.",
        contentsBox: { background: "rgba(220,38,38,0.05)", border: "1px solid rgba(220,38,38,0.20)" },
        contentsEyebrow: "var(--c-red)",
        contentsItemText: "text-[color:var(--c-grey-700)]",
        compareBorder: "1px solid rgba(220,38,38,0.20)",
        compareCells: {
          left:  { background: "rgba(0,0,0,0.04)", labelColor: "text-[color:var(--c-grey-500)]", priceClass: "text-[color:var(--c-grey-500)] line-through" },
          mid:   { background: "rgba(220,38,38,0.10)", labelColor: "text-[color:var(--c-red)]", priceClass: "text-black font-bold" },
          right: { background: "rgba(196,155,80,0.18)", labelColor: "text-[color:var(--c-gold-deep)]", priceClass: "text-black font-bold" },
        },
        trustText: "text-[color:var(--c-grey-700)]",
        trustIcon: "var(--c-red)",
        callout: { label: "Try Before You Buy Larger Quantities", icon: "fa-flask" },
        cta: { background: "var(--c-red)", color: "#fff" },
      }
    : {
        wrapStyle: {
          background: "linear-gradient(180deg, #0a0a0a 0%, #161412 100%)",
          border: "1px solid var(--c-gold)",
          boxShadow: "0 16px 40px -16px rgba(196,155,80,0.45), 0 1px 0 rgba(196,155,80,0.18) inset",
          color: "#fff",
        },
        ribbon: "linear-gradient(90deg, var(--c-gold) 0%, #f5d27a 50%, var(--c-gold) 100%)",
        topBadge: {
          label: "Bundle Deal",
          icon: "fa-gem",
          style: { background: "var(--c-black)", color: "var(--c-gold)", border: "1px solid var(--c-gold)" },
        },
        savingBadge: { background: "var(--c-red)", color: "#fff" },
        eyebrow: "Bundle",
        eyebrowColor: "var(--c-gold)",
        title: "text-white",
        sub: "text-white/70",
        subText: "Perfect for restaurants, takeaways, cafés, food vans and catering businesses.",
        contentsBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(196,155,80,0.25)" },
        contentsEyebrow: "var(--c-gold)",
        contentsItemText: "text-white/85",
        compareBorder: "1px solid rgba(196,155,80,0.25)",
        compareCells: {
          left:  { background: "rgba(255,255,255,0.03)", labelColor: "text-white/55", priceClass: "text-white/55 line-through" },
          mid:   { background: "rgba(196,155,80,0.10)", labelColor: "text-[color:var(--c-gold)]", priceClass: "text-white font-bold" },
          right: { background: "rgba(220,38,38,0.18)", labelColor: "text-white/80", priceClass: "text-white font-bold" },
        },
        trustText: "text-white/70",
        trustIcon: "var(--c-gold)",
        callout: null,
        cta: { background: "var(--c-gold)", color: "var(--c-black)" },
      };

  return (
    <div
      className="relative rounded-2xl overflow-hidden flex flex-col"
      style={T.wrapStyle}
      data-testid={`bundle-card-${p.slug}`}
      data-bundle-theme={theme}
    >
      <div className="absolute top-0 left-0 right-0 h-1.5" style={{ background: T.ribbon }} />

      <Link to={`/product/${p.slug}`} className="relative block aspect-square overflow-hidden" style={{ background: "#fff" }}>
        {p.image ? (
          <img src={p.image} alt={p.name} className="w-full h-full object-contain p-3 transition-transform duration-500 hover:scale-[1.03]" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[color:var(--c-grey-200)]"><i className="fa-solid fa-box-open text-4xl"></i></div>
        )}
        <span
          className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-bold tracking-[0.18em] uppercase shadow"
          style={T.topBadge.style}
          data-testid="bundle-badge"
        >
          <i className={`fa-solid ${T.topBadge.icon} text-[9px] mr-1`} /> {T.topBadge.label}
        </span>
        {hasSaving && (
          <span
            className="absolute top-3 right-3 px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider shadow"
            style={T.savingBadge}
            data-testid="bundle-saving-badge"
          >
            Save £{saving.toFixed(2)} ({savingPct}%)
          </span>
        )}
      </Link>

      <div className="p-5 md:p-6 flex flex-col flex-1">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: T.eyebrowColor }}>{T.eyebrow}</div>
        <Link to={`/product/${p.slug}`} className={`font-display text-lg md:text-xl mt-1 leading-snug hover:underline ${T.title}`}>
          {p.name}
        </Link>
        <p className={`mt-2 text-xs leading-relaxed ${T.sub}`}>
          {T.subText}
        </p>

        {/* Optional inner callout (household theme only) */}
        {T.callout && (
          <div
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.14em] self-start"
            style={{ background: "var(--c-black)", color: "var(--c-gold)" }}
            data-testid="bundle-callout"
          >
            <i className={`fa-solid ${T.callout.icon}`} /> {T.callout.label}
          </div>
        )}

        {items.length > 0 && (
          <div className="mt-4 rounded-xl p-3" style={T.contentsBox} data-testid="bundle-contents">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2" style={{ color: T.contentsEyebrow }}>What's inside</div>
            <ul className="space-y-1.5">
              {items.slice(0, 6).map((item, i) => (
                <li key={i} className={`flex items-start gap-2 text-xs leading-snug ${T.contentsItemText}`}>
                  <i className="fa-solid fa-check mt-0.5 text-[10px]" style={{ color: T.contentsEyebrow }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 grid grid-cols-3 gap-1 rounded-xl overflow-hidden" style={{ border: T.compareBorder }} data-testid="bundle-price-compare">
          <div className="p-2.5 text-center" style={{ background: T.compareCells.left.background }}>
            <div className={`text-[9px] uppercase tracking-wider ${T.compareCells.left.labelColor}`}>Buy separately</div>
            <Money value={compareAt || price} className={`text-sm font-display ${T.compareCells.left.priceClass}`} />
          </div>
          <div className="p-2.5 text-center" style={{ background: T.compareCells.mid.background }}>
            <div className={`text-[9px] uppercase tracking-wider ${T.compareCells.mid.labelColor}`}>Bundle</div>
            <Money value={price} className={`text-base font-display ${T.compareCells.mid.priceClass}`} />
          </div>
          <div className="p-2.5 text-center" style={{ background: T.compareCells.right.background }}>
            <div className={`text-[9px] uppercase tracking-wider ${T.compareCells.right.labelColor}`}>You save</div>
            <span className={`text-sm font-display ${T.compareCells.right.priceClass}`} data-testid="bundle-save-cell">{savingPct}%</span>
          </div>
        </div>

        <div className={`mt-3 grid grid-cols-2 gap-1.5 text-[10px] ${T.trustText}`} data-testid="bundle-trust">
          {[
            { i: "fa-warehouse", t: "UK Warehouse" },
            { i: "fa-truck-fast", t: "Same-Day Dispatch" },
            { i: "fa-shield-halved", t: "Secure Checkout" },
            { i: "fa-piggy-bank", t: "Bulk Savings Included" },
          ].map((x) => (
            <div key={x.t} className="flex items-center gap-1.5">
              <i className={`fa-solid ${x.i}`} style={{ color: T.trustIcon }} /> {x.t}
            </div>
          ))}
        </div>

        <Link
          to={`/product/${p.slug}`}
          className="btn mt-4 !py-3 !text-sm font-semibold"
          style={T.cta}
          data-testid={`bundle-cta-${p.slug}`}
        >
          <i className="fa-solid fa-bag-shopping" /> View Bundle &amp; Add to Cart
        </Link>
      </div>
    </div>
  );
}

/** Compact "Best Value" ribbon used in the dedicated section. */
export function BundleHeaderRibbon() {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em]" style={{ background: "var(--c-black)", color: "var(--c-gold)", border: "1px solid var(--c-gold)" }}>
      <i className="fa-solid fa-crown" /> Best Value for Takeaways &amp; Catering Businesses
    </div>
  );
}

/** Dedicated "Bundle Deals" section, intended for the top of /shop and home page. */
export function BundleDealsSection({ bundles, useCart }) {
  if (!bundles || bundles.length === 0) return null;
  return (
    <section className="py-12 md:py-16" style={{ background: "linear-gradient(180deg, #fefdfb 0%, var(--c-cream) 100%)" }} data-testid="bundle-deals-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6 md:mb-8">
          <div>
            <BundleHeaderRibbon />
            <h2 className="font-display text-3xl md:text-4xl text-black mt-3 leading-tight">Bundle Deals</h2>
            <p className="mt-2 text-sm text-[color:var(--c-grey-700)] max-w-xl">
              Hand-picked combinations of our top sellers, priced lower than buying each item individually. Perfect for restaurants, takeaways, cafés and food businesses.
            </p>
          </div>
          <Link to="/shop?tag=bundle" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold underline" data-testid="bundle-see-all">
            See all bundles <i className="fa-solid fa-arrow-right text-xs" />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {bundles.slice(0, 6).map((b) => (
            <BundleCard key={b.id} p={b} useCart={useCart} />
          ))}
        </div>
      </div>
    </section>
  );
}

// MOCK_BUNDLES removed — bundles render exclusively from real Shopify `tag:bundle` data.

// ---------------------------------------------------------------------------
// Premium bundle Product Detail layout
// ---------------------------------------------------------------------------

/** Parse a Shopify bundle product's description_html into structured sections
 *  so the PDP can render them as headings + bullet cards instead of a single
 *  raw paragraph. Tolerant of Shopify rich-text editor quirks (nested <li>,
 *  `data-start`/`data-end` attrs, stray `<meta>` tags, etc.).
 */
export function parseBundleDescription(html, productName = "") {
  const sections = { intro: [], perfectFor: [], whyChoose: [], included: [] };
  if (!html || typeof window === "undefined" || !window.DOMParser) return sections;

  let root;
  try {
    const doc = new window.DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
    root = doc.body && doc.body.firstChild;
  } catch (e) {
    return sections;
  }
  if (!root) return sections;

  // Strip editor noise so text comparisons stay clean.
  root.querySelectorAll("meta, script, style").forEach((m) => m.remove());

  const labelMap = (text) => {
    const t = (text || "").toLowerCase().trim();
    if (/^(perfect|ideal|great|designed|made|recommended|best)\s+for/.test(t)) return "perfectFor";
    if (/^(includes?|what['’]?s\s+included|in\s+this\s+bundle|what\s+you\s+get|contains|inside)/.test(t)) return "included";
    if (/^(why|reasons?|benefits?|features?|highlights?)/.test(t)) return "whyChoose";
    return null;
  };

  // Reject lines that are pure pricing / saving text (we render those in the
  // dedicated pricing block, never inline).
  const PRICE_PATTERNS = [
    /^\s*£\s*\d/i,
    /^\s*save\s+\d/i,
    /^\s*\(\s*£/i,
    /^\s*\d+\s*%\s*off/i,
    /^\s*bundle\s+price/i,
    /^\s*compare\s+at/i,
  ];
  const isPriceOnly = (text) => {
    const t = (text || "").trim();
    if (!t) return true;
    return PRICE_PATTERNS.some((re) => re.test(t));
  };

  const productNameLower = (productName || "").toLowerCase().trim();
  const normalizeName = (s) =>
    (s || "")
      .toLowerCase()
      .replace(/^crown01\s+/, "")
      .replace(/\s+/g, " ")
      .trim();
  const productNameNorm = normalizeName(productName);
  const isProductNameEcho = (text) => {
    if (!productNameLower) return false;
    const t = (text || "").toLowerCase().replace(/\s+/g, " ").trim();
    if (!t) return true;
    if (t === productNameLower) return true;
    const tn = normalizeName(t);
    return !!(productNameNorm && (tn === productNameNorm || t.includes(productNameNorm) || productNameNorm.includes(tn)));
  };

  const flat = [];
  const walk = (node) => {
    if (!node || !node.children) return;
    for (const child of node.children) {
      const tag = (child.tagName || "").toLowerCase();
      if (tag === "ul" || tag === "ol") {
        const items = Array.from(child.children)
          .filter((c) => (c.tagName || "").toLowerCase() === "li")
          // Take only direct text of the <li>, not nested <ul>/<p> content,
          // because Shopify often nests subsequent sections inside an open <li>.
          .map((li) => {
            const clone = li.cloneNode(true);
            clone.querySelectorAll("ul, ol, p, h1, h2, h3, h4").forEach((n) => n.remove());
            return (clone.textContent || "").replace(/\s+/g, " ").trim();
          })
          .filter(Boolean);
        if (items.length) flat.push({ kind: "list", items });
        // Recurse so any sections nested inside <li> are still discovered.
        walk(child);
        continue;
      }
      if (["p", "h1", "h2", "h3", "h4", "h5", "h6"].includes(tag)) {
        const text = (child.textContent || "").replace(/\s+/g, " ").trim();
        if (text) flat.push({ kind: "para", text });
        // Don't recurse into a paragraph — its text is captured already.
        continue;
      }
      walk(child);
    }
  };
  walk(root);

  let pendingLabel = null;
  for (const tok of flat) {
    if (tok.kind === "para") {
      if (isPriceOnly(tok.text) || isProductNameEcho(tok.text)) continue;
      const label = labelMap(tok.text);
      if (label) {
        pendingLabel = label;
      } else if (!pendingLabel) {
        sections.intro.push(tok.text);
      }
    } else if (tok.kind === "list") {
      const cleaned = tok.items.filter((i) => !isPriceOnly(i) && !isProductNameEcho(i));
      if (!cleaned.length) continue;
      if (pendingLabel) {
        sections[pendingLabel].push(...cleaned);
        pendingLabel = null;
      } else {
        // Unlabeled list — most likely an "Includes" list with the heading lost
        // in markup. Default to `included`.
        sections.included.push(...cleaned);
      }
    }
  }

  // De-dupe everything (case-insensitive).
  for (const key of Object.keys(sections)) {
    const seen = new Set();
    sections[key] = sections[key].filter((item) => {
      const k = item.toLowerCase();
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }

  return sections;
}

/** Pricing row used inside the dedicated bundle pricing block. */
function PriceCell({ label, value, accent, strike, display }) {
  return (
    <div className="p-3 md:p-4 text-center" style={{ background: accent ? "rgba(196,155,80,0.10)" : "rgba(0,0,0,0.04)" }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-semibold" style={{ color: accent ? "var(--c-gold-deep)" : "var(--c-grey-500)" }}>
        {label}
      </div>
      <div className={`font-display text-lg md:text-2xl mt-1 ${strike ? "line-through text-[color:var(--c-grey-500)]" : "text-black"}`}>
        {display !== undefined ? display : `£${Number(value || 0).toFixed(2)}`}
      </div>
    </div>
  );
}

/** Premium PDP layout for products tagged `bundle`. Parses the Shopify product
 *  description into structured sections (Perfect for / Why choose this bundle /
 *  What's included) and renders a dedicated pricing block from variant data.
 *  Replaces the raw `description_html` block on the bundle PDP. */
export function BundleDetailLayout({ p }) {
  const sections = parseBundleDescription(p?.description_html || "", p?.name);
  const variant = p?.variants?.find((v) => v.available) || p?.variants?.[0];
  const price = Number(variant?.price ?? p?.min_price ?? 0);
  const compareAt = Number(resolveBundleCompareAt(p, variant) || 0);
  const hasSaving = compareAt > price && price > 0;
  const saving = hasSaving ? compareAt - price : 0;
  const savingPct = hasSaving ? Math.round((saving / compareAt) * 100) : 0;

  // Use `intro` paragraphs as the "Why choose this bundle" body when no
  // explicit list was found in the description — common for shorter bundles.
  const whyChoose = sections.whyChoose.length ? sections.whyChoose : [];
  const showWhyParagraphs = !whyChoose.length && sections.intro.length;

  const anyContent =
    sections.perfectFor.length ||
    sections.included.length ||
    whyChoose.length ||
    sections.intro.length ||
    hasSaving;

  if (!anyContent) return null;

  return (
    <div className="mt-6 space-y-5 md:space-y-6" data-testid="bundle-detail-layout">
      {/* Perfect for */}
      {sections.perfectFor.length > 0 && (
        <section
          className="rounded-2xl p-4 md:p-5"
          style={{ background: "var(--c-cream)", border: "1px solid rgba(196,155,80,0.30)" }}
          data-testid="bundle-perfect-for"
        >
          <div className="flex items-center gap-2 mb-3">
            <i className="fa-solid fa-heart text-[12px]" style={{ color: "var(--c-gold-deep)" }} />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--c-gold-deep)" }}>
              Perfect for
            </h2>
          </div>
          <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
            {sections.perfectFor.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-[color:var(--c-grey-700)] leading-snug">
                <i className="fa-solid fa-check mt-0.5 text-[10px]" style={{ color: "var(--c-gold-deep)" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Why choose this bundle */}
      {(whyChoose.length > 0 || showWhyParagraphs) && (
        <section
          className="rounded-2xl p-4 md:p-5"
          style={{ background: "var(--c-grey-50)", border: "1px solid var(--c-grey-100)" }}
          data-testid="bundle-why-choose"
        >
          <div className="flex items-center gap-2 mb-3">
            <i className="fa-solid fa-gem text-[12px]" style={{ color: "var(--c-gold-deep)" }} />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-bold text-black">Why choose this bundle</h2>
          </div>
          {whyChoose.length > 0 ? (
            <ul className="space-y-2">
              {whyChoose.map((item, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[color:var(--c-grey-700)] leading-relaxed">
                  <i className="fa-solid fa-check mt-1 text-[10px]" style={{ color: "var(--c-gold-deep)" }} />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="space-y-2 text-sm text-[color:var(--c-grey-700)] leading-relaxed">
              {sections.intro.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
          )}
        </section>
      )}

      {/* What's included */}
      {sections.included.length > 0 && (
        <section
          className="rounded-2xl p-4 md:p-5"
          style={{ background: "var(--c-black)", color: "#fff", border: "1px solid var(--c-gold)" }}
          data-testid="bundle-included"
        >
          <div className="flex items-center gap-2 mb-3">
            <i className="fa-solid fa-box-open text-[12px]" style={{ color: "var(--c-gold)" }} />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--c-gold)" }}>
              What's included
            </h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-x-5 gap-y-2">
            {sections.included.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-white/90 leading-snug">
                <i className="fa-solid fa-check mt-0.5 text-[10px]" style={{ color: "var(--c-gold)" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Bundle pricing */}
      {hasSaving && (
        <section
          className="rounded-2xl overflow-hidden"
          style={{ border: "1px solid var(--c-grey-100)" }}
          data-testid="bundle-pricing-block"
        >
          <div className="px-4 md:px-5 py-3 flex items-center gap-2" style={{ background: "var(--c-cream)" }}>
            <i className="fa-solid fa-tags text-[12px]" style={{ color: "var(--c-gold-deep)" }} />
            <h2 className="text-[11px] uppercase tracking-[0.22em] font-bold" style={{ color: "var(--c-gold-deep)" }}>
              Bundle pricing
            </h2>
            <span
              className="ml-auto px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider"
              style={{ background: "var(--c-red)", color: "#fff" }}
              data-testid="bundle-pricing-save-badge"
            >
              Save £{saving.toFixed(2)} ({savingPct}%)
            </span>
          </div>
          <div className="grid grid-cols-3 gap-px" style={{ background: "var(--c-grey-100)" }}>
            <PriceCell label="Buy separately" value={compareAt} strike />
            <PriceCell label="Bundle price" value={price} accent />
            <PriceCell label="You save" display={`${savingPct}%`} accent />
          </div>
        </section>
      )}
    </div>
  );
}

